import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Instagram,
  LayoutTemplate,
  Link2,
  Loader2,
  Pencil,
  Trash2,
  Type,
  Upload,
  Youtube,
} from 'lucide-react';
import { useI18n } from '@nx/i18n';
import type { ProfileComponent, ProfileComponentConfig, SocialPlatform } from '@nx/domain';
import { parseYoutubeVideoId, youtubeThumbnailUrl } from '../../utils/parseYoutubeVideoId';
import { safeCssBackgroundImageUrl } from '../../utils/safeCssBackgroundImageUrl';

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#C9447A',
  youtube: '#C0392B',
  x: '#1A1A1A',
  twitter: '#1A1A1A',
  linkedin: '#2B5E8C',
};

const SOCIAL_PLATFORMS: SocialPlatform[] = ['instagram', 'youtube', 'twitter', 'linkedin', 'x'];

const SOCIAL_EDITOR_TYPES = new Set<ProfileComponent['type']>(['social_link', 'twitter']);

interface ComponentBlockEditorProps {
  component: ProfileComponent;
  index: number;
  total: number;
  imageUploading: boolean;
  onMove: (index: number, dir: -1 | 1) => void;
  onToggle: (visible: boolean) => void;
  onRemove: () => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  onConfigChange: (config: Partial<ProfileComponentConfig>) => void;
  onPersist: () => void;
  onImageUpload: (file: File) => void;
  onImageRemove: () => void;
}

export function ComponentBlockEditor({
  component,
  index,
  total,
  imageUploading,
  onMove,
  onToggle,
  onRemove,
  onEditStart,
  onEditEnd,
  onConfigChange,
  onPersist,
  onImageUpload,
  onImageRemove,
}: ComponentBlockEditorProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const hidden = !component.visible;
  const { config } = component;

  const clickLabel = component.clickCount
    ? t('profile.stats.clickCount').replace('{count}', String(component.clickCount))
    : component.type;

  const patch = (partial: Partial<ProfileComponentConfig>) => {
    onConfigChange(partial);
  };

  const finishEditSession = () => {
    if (imageUploading) return;
    onPersist();
    onEditEnd();
  };

  const setExpandedState = (next: boolean) => {
    if (next) {
      onEditStart();
      setExpanded(true);
      return;
    }
    finishEditSession();
    setExpanded(false);
  };

  const iconBg = iconColorForType(component.type, config.platform);
  const isSocialEditor = SOCIAL_EDITOR_TYPES.has(component.type);

  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const imageUploadingRef = useRef(imageUploading);
  imageUploadingRef.current = imageUploading;
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;
  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;
  const isRemovingRef = useRef(false);
  const effectInstanceRef = useRef(0);

  useEffect(() => {
    const effectInstance = ++effectInstanceRef.current;
    return () => {
      queueMicrotask(() => {
        if (effectInstanceRef.current !== effectInstance) return;
        if (isRemovingRef.current || !expandedRef.current || imageUploadingRef.current) return;
        onPersistRef.current();
        onEditEndRef.current();
      });
    };
  }, []);

  const handleRemove = () => {
    isRemovingRef.current = true;
    onRemove();
  };

  return (
    <div className={`profile-editor-citem ${hidden ? 'hidden' : ''} ${expanded ? 'expanded' : ''}`}>
      <div className="profile-editor-citem-head">
        <GripVertical className="h-4 w-4 text-[#D4C9BA]" strokeWidth={2} />
        <span className="profile-editor-cic" style={{ backgroundColor: iconBg }}>
          <BlockTypeIcon type={component.type} platform={config.platform} />
        </span>
        <button
          type="button"
          className="profile-editor-cmain"
          onClick={() => setExpandedState(!expanded)}
        >
          <strong>{config.label ?? component.type}</strong>
          <small>{clickLabel}</small>
        </button>
        <span className="profile-editor-cactions">
          <button
            type="button"
            className="profile-editor-iconbtn"
            onClick={() => setExpandedState(!expanded)}
            aria-label={expanded ? t('profile.block.collapse') : t('profile.block.edit')}
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            className="profile-editor-iconbtn"
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
          >
            <ChevronUp className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            className="profile-editor-iconbtn"
            disabled={index === total - 1}
            onClick={() => onMove(index, 1)}
          >
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            className={`profile-editor-toggle ${hidden ? '' : 'on'}`}
            onClick={() => onToggle(hidden)}
            aria-label="Toggle visibility"
          />
          <button type="button" className="profile-editor-iconbtn danger" onClick={handleRemove}>
            <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </span>
      </div>

      {expanded ? (
        <div className="profile-editor-cedit">
          {isSocialEditor ? (
            <>
              <Field label={t('profile.field.blockPlatform')}>
                <select
                  className="profile-editor-select"
                  value={config.platform ?? 'instagram'}
                  onChange={(e) => {
                    const platform = e.target.value as SocialPlatform;
                    patch({ platform });
                    onPersist();
                  }}
                >
                  {SOCIAL_PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
              <TextField
                label={t('profile.field.blockLabel')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockHandle')}
                value={config.handle ?? ''}
                onChange={(handle) => patch({ handle })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockUrl')}
                value={config.url ?? ''}
                onChange={(url) => patch({ url })}
                onBlur={onPersist}
              />
            </>
          ) : null}

          {component.type === 'custom_link' ? (
            <>
              <TextField
                label={t('profile.field.blockLabel')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockNote')}
                value={config.note ?? ''}
                onChange={(note) => patch({ note })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockUrl')}
                value={config.url ?? ''}
                onChange={(url) => patch({ url })}
                onBlur={onPersist}
              />
            </>
          ) : null}

          {component.type === 'youtube' ? (
            <>
              <YoutubeFields
                embedId={config.embedId}
                label={t('profile.field.blockVideoUrl')}
                placeholder={t('profile.field.blockVideoUrlPlaceholder')}
                onBlur={(embedId) => {
                  patch({ embedId: embedId ?? '' });
                  onPersist();
                }}
              />
              <TextField
                label={t('profile.field.blockLabel')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockUrl')}
                value={config.url ?? ''}
                onChange={(url) => patch({ url })}
                onBlur={onPersist}
              />
            </>
          ) : null}

          {component.type === 'instagram' ? (
            <>
              <TextField
                label={t('profile.field.blockUrl')}
                value={config.url ?? ''}
                onChange={(url) => patch({ url })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockLabel')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
              <TextField
                label={t('profile.field.blockCaption')}
                value={config.caption ?? ''}
                onChange={(caption) => patch({ caption })}
                onBlur={onPersist}
              />
            </>
          ) : null}

          {component.type === 'text_block' ? (
            <>
              <TextField
                label={t('profile.field.blockHeading')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
              <Field label={t('profile.field.blockBody')}>
                <textarea
                  className="profile-editor-textarea"
                  value={config.content ?? ''}
                  onChange={(e) => patch({ content: e.target.value })}
                  onBlur={onPersist}
                />
              </Field>
            </>
          ) : null}

          {component.type === 'image_block' ? (
            <>
              <Field label={t('profile.field.blockImage')}>
                {safeCssBackgroundImageUrl(config.imageUrl) ? (
                  <div
                    className="profile-editor-block-thumb"
                    style={
                      {
                        backgroundImage: `url(${safeCssBackgroundImageUrl(config.imageUrl)})`,
                      } as CSSProperties
                    }
                  />
                ) : null}
                <div className="flex gap-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) onImageUpload(file);
                    }}
                  />
                  <button
                    type="button"
                    className="tp-btn tp-btn-outline tp-btn-sm"
                    disabled={imageUploading}
                    onClick={() => imageInputRef.current?.click()}
                  >
                    {imageUploading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                    ) : (
                      <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                    )}
                    {t('profile.action.uploadBlockImage')}
                  </button>
                  {config.imageUrl ? (
                    <button
                      type="button"
                      className="tp-btn tp-btn-ghost tp-btn-sm"
                      disabled={imageUploading}
                      onClick={onImageRemove}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                      {t('profile.action.removeBlockImage')}
                    </button>
                  ) : null}
                </div>
                <p className="profile-editor-help">
                  <span>{t('profile.background.help')}</span>
                </p>
              </Field>
              <TextField
                label={t('profile.field.blockCaption')}
                value={config.label ?? ''}
                onChange={(label) => patch({ label })}
                onBlur={onPersist}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="profile-editor-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return (
    <Field label={label}>
      <input
        className="profile-editor-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    </Field>
  );
}

function YoutubeFields({
  embedId,
  label,
  placeholder,
  onBlur,
}: {
  embedId?: string;
  label: string;
  placeholder: string;
  onBlur: (embedId: string | null) => void;
}) {
  const [videoInput, setVideoInput] = useState(() =>
    embedId && embedId !== 'x' ? `https://www.youtube.com/watch?v=${embedId}` : '',
  );
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    setVideoInput(embedId && embedId !== 'x' ? `https://www.youtube.com/watch?v=${embedId}` : '');
  }, [embedId]);

  const previewId = parseYoutubeVideoId(videoInput);

  return (
    <Field label={label}>
      <input
        className="profile-editor-input"
        value={videoInput}
        placeholder={placeholder}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => setVideoInput(e.target.value)}
        onBlur={(e) => {
          focusedRef.current = false;
          onBlur(parseYoutubeVideoId(e.target.value));
        }}
      />
      {previewId ? (
        <div
          className="profile-editor-block-thumb profile-editor-block-thumb--yt"
          style={{ backgroundImage: `url(${youtubeThumbnailUrl(previewId)})` } as CSSProperties}
        />
      ) : null}
    </Field>
  );
}

function iconColorForType(type: ProfileComponent['type'], platform?: string): string {
  if (SOCIAL_EDITOR_TYPES.has(type)) return PLATFORM_COLORS[platform ?? ''] ?? '#6B7A5A';
  if (type === 'youtube') return PLATFORM_COLORS.youtube;
  if (type === 'instagram') return PLATFORM_COLORS.instagram;
  if (type === 'text_block') return '#78665A';
  if (type === 'image_block') return '#6B7A5A';
  return '#6B7A5A';
}

function BlockTypeIcon({
  type,
  platform,
}: {
  type: ProfileComponent['type'];
  platform?: string;
}) {
  const cls = 'h-4 w-4';
  if (type === 'youtube' || platform === 'youtube') {
    return <Youtube className={cls} strokeWidth={2} />;
  }
  if (type === 'instagram' || platform === 'instagram' || type === 'twitter' || platform === 'twitter') {
    return <Instagram className={cls} strokeWidth={2} />;
  }
  if (type === 'text_block') return <Type className={cls} strokeWidth={2} />;
  if (type === 'image_block') return <LayoutTemplate className={cls} strokeWidth={2} />;
  return <Link2 className={cls} strokeWidth={2} />;
}
